import React, { useState, useEffect, useRef } from 'react';
import { useToast } from '@/hooks/use-toast';
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from '@/components/ui/toast';
import { Check, Copy } from 'lucide-react';
import { UnifiedButton } from '@/components/ui/UnifiedButton';
import { apiRequest } from '@/lib/queryClient';

// Session tracking for error context
const sessionStartTime = Date.now();
let previousPage = document.referrer || 'direct';

// Track page navigation for context
if (typeof window !== 'undefined') {
  const originalPushState = history.pushState;
  history.pushState = function (...args) {
    previousPage = window.location.href;
    return originalPushState.apply(this, args);
  };
}

// Get network connection info (if available)
function getNetworkInfo(): { type: string; effectiveType?: string; downlink?: number } {
  const nav = navigator as Navigator & {
    connection?: { type?: string; effectiveType?: string; downlink?: number };
  };
  if (nav.connection) {
    return {
      type: nav.connection.type || 'unknown',
      effectiveType: nav.connection.effectiveType,
      downlink: nav.connection.downlink,
    };
  }
  return { type: 'unknown' };
}

// Get memory usage (if available)
function getMemoryInfo(): { usedJSHeapSize?: number; totalJSHeapSize?: number } | null {
  const perf = performance as Performance & {
    memory?: { usedJSHeapSize: number; totalJSHeapSize: number };
  };
  if (perf.memory) {
    return {
      usedJSHeapSize: Math.round(perf.memory.usedJSHeapSize / 1024 / 1024),
      totalJSHeapSize: Math.round(perf.memory.totalJSHeapSize / 1024 / 1024),
    };
  }
  return null;
}

// Expected errors that shouldn't be auto-reported (user errors, not system bugs)
const EXPECTED_ERROR_PATTERNS = [
  /invalid (credentials|email|username|password)/i,
  /incorrect.*password/i,
  /account.*not found/i,
  /please.*sign in/i,
  /session.*expired/i,
  /unauthorized/i,
  /access denied/i,
  /permission denied/i,
  /already exists/i,
  /duplicate/i,
  /validation.*failed/i,
  /required field/i,
  /invalid.*format/i,
  /too (short|long|many|few)/i,
  // Verification token errors — expected user behavior (expired/reused links)
  /invalid or expired verif/i,
  /verification.*expired/i,
  /verification.*already.*used/i,
  /verification.*failed/i,
  // Health check / monitoring results — not user-facing bugs
  /healthy.*false/i,
  /service unavailable/i,
  // Module loading errors — transient deployment artifacts
  /importing a module script failed/i,
  /dynamically imported module/i,
  /failed to fetch dynamically/i,
  /loading chunk/i,
];

/**
 * Check if an error message is expected behavior (not a bug)
 * These are user errors or auth flows, not system problems
 */
function isExpectedError(errorMessage: string): boolean {
  return EXPECTED_ERROR_PATTERNS.some(pattern => pattern.test(errorMessage));
}

// Track in-flight tech report request to prevent self-referential loop
let isReportingError = false;

// Auto-send errors to tech team (no user interaction needed)
async function autoReportError(errorMessage: string, errorCode?: string) {
  // Prevent self-referential loop: if a tech-report request itself fails,
  // don't try to report that failure (which would fail again, etc.)
  if (isReportingError) return;

  try {
    isReportingError = true;
    const sessionDuration = Math.round((Date.now() - sessionStartTime) / 1000);
    const networkInfo = getNetworkInfo();
    const memoryInfo = getMemoryInfo();

    const context = {
      url: window.location.href,
      timestamp: new Date().toISOString(),
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      userAgent: navigator.userAgent,
      // Enhanced context
      sessionDuration, // seconds since page load
      previousPage,
      networkType: networkInfo.type,
      networkEffectiveType: networkInfo.effectiveType,
      networkDownlink: networkInfo.downlink,
      memoryUsedMB: memoryInfo?.usedJSHeapSize,
      memoryTotalMB: memoryInfo?.totalJSHeapSize,
      language: navigator.language,
      cookiesEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
    };

    await apiRequest('POST', '/api/tech-reports', {
      errorMessage,
      errorCode,
      source: 'api_error', // Auto-reported from toast (uses api_error source type)
      url: window.location.href,
      additionalContext: context,
    });
  } catch (err) {
    // Silent fail - don't show another toast for failed reporting
    console.warn('[AutoReport] Failed to send error report:', err);
  } finally {
    isReportingError = false;
  }
}

function CopyToClipboardButton({ errorMessage }: { errorMessage: string }) {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      await navigator.clipboard.writeText(errorMessage);
      setCopied(true);

      toast({
        title: 'Copied to Clipboard',
        description: 'Error message copied. You can now paste it anywhere.',
        duration: 2000,
      });

      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('[CopyToClipboard] Failed:', err);
      toast({
        title: 'Copy Failed',
        description: 'Could not copy to clipboard. Please select and copy manually.',
        variant: 'destructive',
      });
    }
  };

  return (
    <UnifiedButton
      type="button"
      onClick={handleCopy}
      onPointerDown={e => e.stopPropagation()}
      disabled={copied}
      className="flex-shrink-0 gap-1.5 px-3 py-2 rounded-[1.3rem] bg-cyan-500/20 hover:bg-cyan-500/30 active:bg-cyan-500/40 transition-colors z-10 cursor-pointer select-none border border-cyan-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
      title={copied ? 'Copied!' : 'Copy error to clipboard'}
      aria-label={copied ? 'Copied to clipboard' : 'Copy error to clipboard'}
    >
      {copied ? (
        <>
          <Check className="h-4 w-4 text-green-400" />
          <span className="text-xs text-green-400 font-medium">Copied!</span>
        </>
      ) : (
        <>
          <Copy className="h-4 w-4 text-cyan-400" />
          <span className="text-xs text-cyan-400 font-medium">Copy</span>
        </>
      )}
    </UnifiedButton>
  );
}

export function Toaster() {
  const reportedErrors = useRef<Set<string>>(new Set());
  const { toasts } = useToast();

  // Auto-report destructive toasts to tech team (excluding expected user errors)
  useEffect(() => {
    toasts.forEach(({ id, title, description, variant }) => {
      if (variant === 'destructive') {
        const errorText = `${title ? String(title) : ''}${title && description ? ': ' : ''}${description ? String(description) : ''}`;

        // Only report each unique error once per session
        if (errorText && !reportedErrors.current.has(id)) {
          reportedErrors.current.add(id);

          // Skip expected errors (auth failures, validation, user mistakes)
          if (isExpectedError(errorText)) {
            return;
          }

          // Extract error code if present
          const errorCodeMatch = errorText.match(/\[([A-Z_]+)\]|\b([0-9]{3})\b/);
          const errorCode = errorCodeMatch ? errorCodeMatch[1] || errorCodeMatch[2] : undefined;

          // Auto-send to tech team (fire and forget)
          autoReportError(errorText, errorCode);
        }
      }
    });
  }, [toasts]);

  return (
    <ToastProvider>
      {toasts.map(function ({ id, title, description, action, ...props }) {
        const isDestructive = props.variant === 'destructive';

        // Build error text for copy button
        const errorText =
          isDestructive && (title || description)
            ? `${title ? String(title) : ''}${title && description ? ': ' : ''}${description ? String(description) : ''}`
            : '';

        return (
          <Toast key={id} {...props}>
            <div className="flex items-start gap-3 w-full pr-6">
              <div className="grid gap-1 flex-1 min-w-0">
                {title && <ToastTitle>{title}</ToastTitle>}
                {description && (
                  <ToastDescription className="break-words whitespace-pre-wrap">
                    {description}
                  </ToastDescription>
                )}
              </div>
              {isDestructive && errorText && (
                <div className="flex gap-2 flex-shrink-0">
                  <CopyToClipboardButton errorMessage={errorText} />
                </div>
              )}
              {action}
            </div>
            <ToastClose />
          </Toast>
        );
      })}
      <ToastViewport />
    </ToastProvider>
  );
}
